import React, { useState } from 'react';
import { Ship, Plane, Truck, TrainFront, Route } from 'lucide-react';
import FoundryButton from './FoundryButton';
import CountryRuleDestinations from './CountryRuleDestinations';
import { CountryRuleChanges, CountryRuleOptions, countryRuleControl as control } from './countryRuleTypes';
const icons: {
  [key: string]: typeof Ship;
} = {
  Ship,
  Plane,
  Truck,
  TrainFront,
  Route
};
const allowedModalities = new Set(['Sea', 'Rail', 'Road', 'Multimodal', 'Air']);
export default function CountryRuleEditor({
  initial,
  options,
  busy,
  error,
  onSubmit,
  onCancel
}: {
  initial: CountryRuleChanges;
  options: CountryRuleOptions;
  busy: boolean;
  error: string;
  onSubmit: (changes: CountryRuleChanges) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<CountryRuleChanges>({
    color: initial.color,
    country: initial.country,
    modelRole: initial.modelRole,
    category: initial.category,
    action: initial.action,
    destinations: initial.destinations,
    modalities: initial.modalities.filter(value => allowedModalities.has(value)),
    customCategory: false,
    customAction: false
  });
  const choices = options.colors.find(entry => entry.color === draft.color);
  const update = (field: keyof CountryRuleChanges, value: unknown) => setDraft(current => ({
    ...current,
    [field]: value
  }));
  const renderChoice = (field: 'category' | 'action') => {
    const custom = field === 'category' ? 'customCategory' : 'customAction';
    const label = field === 'category' ? 'Categorie' : 'Actie';
    const values = field === 'category' ? choices?.categories : choices?.actions;
    return <div className="min-w-0">
      <label className="text-sm font-medium">{label}
        <select required value={draft[custom] ? '__custom__' : draft[field]} className={control}
          onChange={e => setDraft(current => ({...current, [custom]: e.target.value === '__custom__', [field]: e.target.value === '__custom__' ? '' : e.target.value}))}>
          <option value="">Selecteer {label.toLocaleLowerCase()}</option>
          {values?.map(value => <option key={value}>{value}</option>)}
          <option value="__custom__">Zelf invullen</option>
        </select>
      </label>
      {draft[custom] && <label className="mt-3 block text-sm">Eigen {label.toLocaleLowerCase()}
        <input required maxLength={field === 'category' ? 100 : 1000} value={draft[field]} onChange={e => update(field, e.target.value)} className={control}/>
      </label>}
    </div>;
  };
  return <form onSubmit={e => {e.preventDefault(); onSubmit(draft);}}>
    <fieldset disabled={busy} className="mt-4 space-y-5">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(140px,0.6fr)_minmax(0,1.4fr)]">
        <label className="min-w-0 text-sm font-medium">Land
          <input required value={draft.country} maxLength={150} onChange={e => update('country', e.target.value)} className={control}/>
        </label>
        <label className="min-w-0 text-sm font-medium">Kleur
          <select required value={draft.color} className={control} onChange={e => setDraft(current => ({...current, color:e.target.value, category:'', action:'', customCategory:false, customAction:false}))}>
            {options.colors.map(entry => <option key={entry.color}>{entry.color}</option>)}
          </select>
        </label>
        {renderChoice('category')}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="min-w-0 text-sm font-medium">Functie in risicomodel
          <input maxLength={500} value={draft.modelRole || ''} onChange={e => update('modelRole', e.target.value)} className={control}/>
        </label>
        {renderChoice('action')}
      </div>
      <fieldset>
        <legend className="mb-2 text-sm font-medium">Modaliteiten</legend>
        <div className="flex flex-wrap gap-2">
          {options.modalities.filter(option => allowedModalities.has(option.value)).map(option => {
            const Icon = icons[option.icon] || Route;
            return <label key={option.value} className="flex cursor-pointer items-center gap-2 border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-950">
              <input type="checkbox" className="accent-orange-500" checked={draft.modalities.includes(option.value)} onChange={e => update('modalities', e.target.checked ? [...draft.modalities,option.value] : draft.modalities.filter(value => value !== option.value))}/>
              <Icon className="h-4 w-4 text-slate-500" aria-hidden="true"/>{option.label}
            </label>;
          })}
        </div>
      </fieldset>
      <section className="min-w-0 border-t border-slate-200 pt-4 dark:border-white/10">
        <h4 className="mb-2 text-sm font-medium">Ports/hubs/freezones</h4>
        <CountryRuleDestinations value={draft.destinations} country={draft.country} ports={options.ports} disabled={busy} onChange={items => update('destinations', items)}/>
      </section>
    </fieldset>
    {error && <p role="alert" className="mt-4 text-sm text-red-700 dark:text-red-300">{error}</p>}
    <div className="mt-5 flex justify-end gap-4 border-t border-slate-200 pt-4 dark:border-white/10">
      <FoundryButton type="button" disabled={busy} onClick={onCancel} className="h-10 w-28">Annuleren</FoundryButton>
      <FoundryButton type="submit" variant="primary" disabled={busy} className="h-10 w-28">{busy ? 'Opslaan…' : 'Opslaan'}</FoundryButton>
    </div>
  </form>;
}
